// Models
import { ShortRuleResponse } from 'app/modules/rules/types';

export enum BlacklistActionTypes {
  RETRIEVE_BLACKLISTS = '@@blacklists/RETRIEVE_BLACKLISTS',
  RETRIEVE_BLACKLISTS_SUCCESS = '@@blacklists/RETRIEVE_BLACKLISTS_SUCCESS',

  CREATE_BLACKLIST = '@@blacklists/CREATE_BLACKLIST',
  CREATE_BLACKLIST_SUCCESS = '@@blacklists/CREATE_BLACKLIST_SUCCESS',
}

export type BlacklistType =
  | 'STRING'
  | 'IP_INET'
  | 'IP_CIDR'
  | 'USER'
  | 'BUSINESS';

export type BlacklistStatus = 'ACTIVE' | 'INACTIVE';

export interface Blacklist {
  id: number;
  name: string;
  description: string;
  type: BlacklistType;
  use_as_whitelist: boolean;
  status: BlacklistStatus;
  u21_global: boolean;
  rules: ShortRuleResponse[];
}

export interface BlacklistsState {
  blacklists: Blacklist[];
  blacklistsCount: number;
}

interface BlacklistItemBase {
  id: number;
  blacklist_id: number;
  created_at: string;
  deactivated_at: string | null;
}

export interface BlacklistStringItem extends BlacklistItemBase {
  value: string;
}

export interface BlacklistIPItem extends BlacklistItemBase {
  source: string;
  value: string;
}

export interface BlacklistUserItem extends BlacklistItemBase {
  alias_first_name: string | null;
  alias_last_name: string | null;
  alias_middle_name: string | null;
  city: string | null;
  client_fingerprint: string;
  country: string | null;
  date_of_birth: string | null;
  email: string | null;
  entity_status: string | null;
  entity_subtype: string | null;
  first_name: string | null;
  ip_address: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone_number: string | null;
  registered_at: string | null;
  ssn: string | null;
  state: string | null;
  street: string | null;
  zip_code: string | null;
}

export interface BlacklistBusinessItem extends BlacklistItemBase {
  business_name: string | null;
  city: string | null;
  corporate_tax_id: string | null;
  country: string | null;
  dba_name: string | null;
  state: string | null;
  street: string | null;
  zip_code: string | null;
}

export type BlacklistItem =
  | BlacklistStringItem
  | BlacklistIPItem
  | BlacklistUserItem
  | BlacklistBusinessItem;

export type FinCEN314aScanType = 'PERSON' | 'BUSINESS';

export type FinCEN314aScanMatchingLogicVersion = 'v1' | 'v2' | 'v3';

export type FinCEN314aPersonScanMatchFieldName =
  | 'DOB'
  | 'FULL_NAME'
  | 'SSN'
  | 'EMAIL'
  | 'IP_ADDRESS'
  | 'WEBSITE'
  | 'ADDRESS'
  | 'PASSPORT'
  | 'DRIVERS_LICENSE';

export interface FinCEN314aPersonScanMatchField {
  field: FinCEN314aPersonScanMatchFieldName;
  threshold: number;
}

export type FinCEN314aPersonScanFuzzyMatchThresholds = {
  [F in FinCEN314aPersonScanMatchFieldName]?: number;
};

export type FinCEN314aEntityFilterOperator = 'is' | 'is_not';

export interface FinCEN314aEntityFilterItem {
  field?: string;
  operator: FinCEN314aEntityFilterOperator;
  value: string;
}

export interface FinCEN314aPersonScanMatchSettings {
  alert_queue?: number;
  fuzzy_match_thresholds?: FinCEN314aPersonScanFuzzyMatchThresholds;
  match_fields?: FinCEN314aPersonScanMatchField[];
  entity_filters?: FinCEN314aEntityFilterItem[];
  matching_logic_version: FinCEN314aScanMatchingLogicVersion;
}

export type FinCEN314aBusinessScanMatchFieldName =
  | 'NAME'
  | 'EMAIL'
  | 'IP_ADDRESS'
  | 'WEBSITE'
  | 'ADDRESS'
  | 'TIN/EIN';

export interface FinCEN314aBusinessScanMatchField {
  field: FinCEN314aBusinessScanMatchFieldName;
  threshold: number;
}

export type FinCEN314aBusinessScanFuzzyMatchThresholds = {
  [F in FinCEN314aBusinessScanMatchFieldName]?: number;
};

export interface FinCEN314aBusinessScanMatchSettings {
  alert_queue?: number;
  fuzzy_match_thresholds?: FinCEN314aBusinessScanFuzzyMatchThresholds;
  match_fields?: FinCEN314aBusinessScanMatchField[];
  entity_filters?: FinCEN314aEntityFilterItem[];
  matching_logic_version: FinCEN314aScanMatchingLogicVersion;
}

export interface FinCEN314aScanConfig {
  person: Omit<FinCEN314aPersonScanMatchSettings, 'matching_logic_version'>;
  business: Omit<FinCEN314aBusinessScanMatchSettings, 'matching_logic_version'>;
}
